import { MutationCache, QueryCache, QueryClient } from '@tanstack/react-query'
import { useViewerStore } from '@/entities/viewer'
import { isNetworkError, isTelegramAuthError, isUserBlockedError } from '@/shared/api'

/**
 * Отказ подписи Telegram или блокировка пользователя на любом запросе — общие
 * для всего приложения: та же `initData` уходит в каждом запросе, а блокировку
 * бэкенд проверяет сразу за подписью. Оболочка (`auth-gate.tsx`) по ним
 * показывает «Откройте заново», «Аккаунт заблокирован» или ошибку приложения
 * вместо экранов.
 */
const reportAuthFailure = (error: unknown): void => {
  if (isTelegramAuthError(error) || isUserBlockedError(error)) {
    useViewerStore.getState().setAuthError(error)
  }
}

/**
 * Дефолты серверного состояния — задаются один раз здесь.
 *
 * Повтор — только сетевых ошибок: детерминированный 4xx даст тот же ответ, а
 * 401 по подписи Telegram повторять бессмысленно — `initData` не меняется до
 * перезапуска Mini App, и ретрай превращается в цикл.
 */
export const queryClient = new QueryClient({
  queryCache: new QueryCache({ onError: reportAuthFailure }),
  mutationCache: new MutationCache({ onError: reportAuthFailure }),
  defaultOptions: {
    queries: {
      staleTime: 30_000,
      gcTime: 5 * 60_000,
      retry: (failureCount, error) => isNetworkError(error) && failureCount < 2,
      retryDelay: (attempt) => Math.min(1000 * 2 ** attempt, 8000),
      // При возврате в Mini App экран перечитывает состояние: так даритель
      // видит, что подарок открыт, без фонового поллинга.
      refetchOnWindowFocus: true,
    },
    mutations: {
      retry: false,
    },
  },
})
